"use client";

import { useQuery } from "@tanstack/react-query";
import { useRealtimeRunWithStreams } from "@trigger.dev/react-hooks";
import { marked } from "marked";
import dynamic from "next/dynamic";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@workspace/ui";
import { buttonVariants } from "@workspace/ui/components/button";
import { Icons } from "@workspace/ui/components/icons";
import { Skeleton } from "@workspace/ui/components/skeleton";

import { mdxVariants } from "~/components/common/content/mdx-styles";
import { Section } from "~/components/common/layout/section";
import { TurboLink } from "~/components/common/turbo-link";
import { getContentMdx } from "~/components/marketing/free-tools/ideas/actions";
import { IdeasLayout } from "~/components/marketing/free-tools/ideas/layout";
import {
  IdeaCard,
  IdeaVotes,
} from "~/components/marketing/free-tools/ideas/list/card";
import { pathsConfig } from "~/config/paths";
import { TYPES } from "~/lib/free-tools/ideas";
import { useTRPC } from "~/trpc/react";

import type { SelectIdea } from "@workspace/db/schema";
import type { IdeaType } from "~/lib/free-tools/ideas";
import type {
  generateIdeaContentStreaming,
  STREAMS,
} from "~/lib/tasks/free-tools/ideas";

// mdx-bundler/client breaks Turbopack SSR on Vercel — load only in the browser.
const MdxContent = dynamic(
  () =>
    import("~/components/common/content/mdx-client").then(
      (module) => module.MdxContent,
    ),
  { ssr: false },
);

const MdxLayout = dynamic(
  () =>
    import("~/components/common/content/mdx-client").then(
      (module) => module.MdxLayout,
    ),
  { ssr: false },
);

const StreamingMarkdown = memo<{ content: string }>(({ content }) => {
  const html = useMemo(() => marked.parse(content) as string, [content]);

  return (
    <MdxLayout>
      <div
        className={cn(mdxVariants())}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </MdxLayout>
  );
});

const useThrottle = (value: string, limit: number) => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRan = useRef(Date.now());

  useEffect(() => {
    const handler = setTimeout(
      function handler() {
        if (Date.now() - lastRan.current >= limit) {
          setThrottledValue(value);
          lastRan.current = Date.now();
        }
      },
      limit - (Date.now() - lastRan.current),
    );

    return () => {
      clearTimeout(handler);
    };
  }, [value, limit]);

  return throttledValue;
};

interface IdeaLayoutProps {
  readonly slug: string;
  readonly initialData?: Omit<SelectIdea, "content">;
  readonly children: React.ReactNode;
}

export const IdeaLayout = memo<IdeaLayoutProps>(
  ({ slug, initialData, children }) => {
    const trpc = useTRPC();
    const { data: idea } = useQuery({
      ...trpc.tools.ideas.getBySlug.queryOptions({ slug }),
      initialData,
    });

    if (!idea) {
      return null;
    }

    return (
      <IdeasLayout
        title={idea.name}
        description={idea.description}
        badge={{
          text: `Explore other ${TYPES[idea.type].label} SaaS ideas`,
          href: `/ideas/${idea.type.toLowerCase()}`,
        }}
        after={<MoreIdeas type={idea.type} />}
      >
        <div className="-mt-4 flex items-center justify-center gap-3 md:-mt-6">
          <TurboLink
            href={pathsConfig.freeTools.ideas.type(idea.type.toLowerCase())}
            className="bg-accent flex h-full items-center gap-2 rounded-lg px-3"
          >
            <span>{TYPES[idea.type].emoji}</span>
            <span>{TYPES[idea.type].label}</span>
          </TurboLink>
          <IdeaVotes
            id={idea.id}
            slug={idea.slug}
            votes={idea.votes}
            className="px-3.5 text-lg"
            iconClassName="size-4"
          />
        </div>
        <div className="py-8 md:py-12 lg:py-14">{children}</div>
      </IdeasLayout>
    );
  },
);

const MoreIdeas = ({ type }: { readonly type: IdeaType }) => {
  const trpc = useTRPC();
  const { data: ideas, isLoading } = useQuery(
    trpc.tools.ideas.getAll.queryOptions({
      orderBy: "createdAt",
      type,
    }),
  );

  return (
    <Section
      id="more-ideas"
      className="bg-muted/50 !col-span-full grid grid-cols-subgrid"
    >
      <div className="col-start-2 flex flex-col items-center justify-center gap-4 md:gap-6">
        <h2 className="max-w-2xl text-center text-3xl font-medium tracking-tight text-balance sm:text-4xl md:text-5xl">
          More {TYPES[type].emoji} {TYPES[type].label} SaaS ideas
        </h2>

        <p className="text-muted-foreground max-w-3xl text-center text-sm text-pretty md:text-base">
          Discover more innovative {TYPES[type].label.toLowerCase()} SaaS ideas
          that are trending in {new Date().getFullYear()}. Each idea is
          AI-generated with market validation and growth potential to help you
          find your next profitable venture faster than competitors.
        </p>
      </div>

      <div className="col-start-2 grid w-full [grid-template-columns:repeat(auto-fill,minmax(18rem,1fr))] gap-4">
        {ideas?.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} />
        ))}

        {isLoading
          ? Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="aspect-[4/3] rounded-xl" />
            ))
          : null}
      </div>

      <TurboLink
        href={pathsConfig.freeTools.ideas.type(type.toLowerCase())}
        className={cn(
          buttonVariants({ variant: "outline" }),
          "bg-card dark:hover:bg-card/75 col-start-2 -mt-3 gap-2 rounded-xl px-4 shadow-xs md:-mt-5",
        )}
      >
        <Icons.Plus className="text-primary size-4" />
        See all ideas
      </TurboLink>
    </Section>
  );
};

const useMdxContent = ({
  slug,
  content,
}: {
  readonly slug: string;
  readonly content: string;
}) =>
  useQuery({
    queryKey: ["ideas", slug, "content", content],
    queryFn: async () => getContentMdx(content),
    staleTime: Infinity,
    retry: false,
  });

interface IdeaRealtimeContentProps {
  readonly slug: string;
  readonly runId: string;
}

export const IdeaRealtimeContent = memo<IdeaRealtimeContentProps>(
  ({ slug, runId }) => {
    const { streams, error, run } = useRealtimeRunWithStreams<
      typeof generateIdeaContentStreaming,
      STREAMS
    >(runId);

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const value = streams?.content?.join("") ?? "";
    const throttledContent = useThrottle(value, 1000);
    const isComplete = Boolean(run?.finishedAt);

    if (!throttledContent.length) {
      return <ContentLoading />;
    }

    if (error) {
      return <ContentError />;
    }

    if (!isComplete) {
      return <StreamingMarkdown content={throttledContent} />;
    }

    return <CompletedMdxContent content={value} slug={slug} />;
  },
);

const CompletedMdxContent = memo<{ content: string; slug: string }>(
  ({ content, slug }) => {
    const mdx = useMdxContent({ slug, content });

    if (mdx.isPending) {
      return <StreamingMarkdown content={content} />;
    }

    if (mdx.data) {
      return (
        <MdxLayout>
          <MdxContent body={mdx.data} />
        </MdxLayout>
      );
    }

    return <StreamingMarkdown content={content} />;
  },
);

const ContentLoading = () => (
  <div className="mx-auto flex w-full max-w-[860px] flex-col items-start gap-8">
    <div className="flex w-full flex-col gap-3">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-8 w-1/2" />
    </div>

    <div className="flex w-full flex-col gap-3">
      <Skeleton className="h-8 w-1/4" />
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-8 w-1/3" />
    </div>

    <div className="flex w-full flex-col gap-3">
      <Skeleton className="h-8 w-4/5" />
      <Skeleton className="h-8 w-3/5" />
      <Skeleton className="h-8 w-2/5" />
    </div>

    <div className="flex w-full flex-col gap-3">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-8 w-full" />
    </div>

    <div className="flex w-full flex-col gap-3">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-8 w-full" />
    </div>
  </div>
);

const ContentError = () => (
  <div className="border-destructive relative mx-auto flex w-full max-w-[860px] flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-5 py-10 md:aspect-square">
    <Icons.CircleX className="text-destructive size-16 stroke-1 md:size-20 lg:size-24" />
    <span className="text-center text-2xl leading-tight font-medium md:text-3xl lg:text-4xl">
      The content should be here...
    </span>
    <p className="text-muted-foreground max-w-lg text-center text-sm md:text-base">
      ...but it seems like something went wrong. Please try again later or
      contact us if the problem persists.
    </p>

    <TurboLink
      href={pathsConfig.freeTools.ideas.index}
      className={cn(buttonVariants({ variant: "outline" }), "mt-6 gap-2")}
    >
      <Icons.ArrowLeft className="size-4" />
      <span>Go back</span>
    </TurboLink>
  </div>
);
